<template>
  <div>
    <div class="overflow-y-auto h-full max-h-[calc(100%-20px)] controls">
      <div class="controls-row">
        <label>배드 크기:</label>
        <input
          class="py-[2px] px-[4px] border border-stone-400"
          v-model.number="bedWidth"
          type="number"
          min="50"
          step="10"
          @change="saveSettings"
        />
        x
        <input
          class="py-[2px] px-[4px] border border-stone-400"
          v-model.number="bedDepth"
          type="number"
          min="50"
          step="10"
          @change="saveSettings"
        />
      </div>

      <div class="controls-row">
        <label>간격(mm):</label>
        <input
          class="py-[2px] px-[4px] border border-stone-400"
          v-model.number="partSpacing"
          type="number"
          min="0"
          @change="saveSettings"
        />
      </div>

      <div class="controls-row">
        <label>탐색범위(%):</label>
        <input
          class="py-[2px] px-[4px] border border-stone-400"
          v-model.number="searchMarginPercent"
          type="number"
          min="0"
          max="100"
          @change="saveSettings"
        />
      </div>

      <div class="controls-row">
        <label>셀 사이즈(mm):</label>
        <input
          class="py-[2px] px-[4px] border border-stone-400"
          v-model.number="cellSize"
          type="number"
          min="1"
          step="1"
          @change="saveSettings"
        />
      </div>

      <div class="controls-row">
        <label>장비사용료:</label>
        <input
          class="py-[2px] px-[4px] border border-stone-400"
          v-model.number="equipmentCost"
          type="number"
          min="0"
          step="10"
          @change="saveSettings"
        />
        <span>원</span>
      </div>

      <div class="controls-row">
        <label>재료비:</label>
        <input
          class="py-[2px] px-[4px] border border-stone-400"
          v-model.number="materialCost"
          type="number"
          min="0"
          step="10"
          @change="saveSettings"
        />
        <span>원</span>
      </div>

      <div class="controls-row">
        <button
          class="px-4 py-2 rounded-[3px] bg-blue-500 text-white"
          style="font-size: 0.95rem; font-weight: 500"
          @click="startPacking"
        >
          계산 시작
        </button>
      </div>

      <Pa12ModelList
        v-if="estimateId"
        class=""
        :estimate-id="estimateId"
        @add-to-packing="addPa12ModelToPacking"
      />

      <div class="box-list">
        <div class="flex justify-between items-center mb-2">
          <p>📦 박스 리스트 (총 {{ parts.length }}개):</p>
          <button
            v-if="parts.length > 0"
            @click="clearAllBoxes"
            class="px-2 py-1 text-xs text-white bg-red-500 rounded hover:bg-red-600"
          >
            전체 삭제
          </button>
        </div>
        <ul class="mt-3">
          <li v-for="(part, index) in parts" :key="index">
            <span
              class="min-w-[120px] max-w-[120px] overflow-hidden text-ellipsis whitespace-nowrap"
              >{{ part.name }}</span
            >
            <span class="text-left text-zinc-500">
              [{{ part.dimensions.map((d) => d.toFixed(1)).join(", ") }}]</span
            >
            <button @click="removeBox(index)" class="remove-btn">삭제</button>
          </li>
        </ul>
      </div>
    </div>

    <div class="info">{{ timingInfo }}</div>
    <div class="results-list">
      <p class="text-sm">📋 계산 결과 히스토리 (총 {{ results.length }}회):</p>
      <ul class="pl-2 mt-3">
        <li v-for="(r, i) in results.slice().reverse()" :key="i">
          <strong>#{{ results.length - i - 1 }}</strong>
          - 시간: {{ r.time }}ms | 셀 사이즈: {{ r.cellSize }} | 탐색범위: {{ r.margin }} | 박스:
          {{ r.count }}개 | 높이: {{ r.height }}mm | 전체무게: {{ r.totalVolume }}g | 예상금액:
          {{ numberToComma(r.finalCost) }}원
        </li>
      </ul>
    </div>
    <div ref="container" class="viewer-container"></div>
  </div>
</template>

<script setup>
import {ref, onMounted, onBeforeUnmount} from "vue";
import * as THREE from "three";
import {OrbitControls} from "three/examples/jsm/controls/OrbitControls";
import Pa12ModelList from "./components/Pa12ModelList.vue";
import {calculateMaterialCost} from "../js/ban-parking";
import {useRoute} from "vue-router";
import {numberToComma} from "../js/utils";

const route = useRoute();

// 라우터에서 estimateId 가져오기
const estimateId = String(route.query.estimateId);

// 상태 및 컨트롤러 참조
const container = ref(null);
const timingInfo = ref("");
const results = ref([]);
const parts = ref([]);
const fileInput = ref(null);

// 설정값
const bedWidth = ref(200);
const bedDepth = ref(200);
const boxCount = ref(0);
const partSpacing = ref(10);
const searchMarginPercent = ref(70); // 탐색범위 비율
const cellSize = ref(7); // ← 추가된 셀 사이즈
const equipmentCost = ref(0); // 장비사용료
const materialCost = ref(0); // 재료비

// localStorage에서 설정값 불러오기
function loadSettings() {
  try {
    const savedWidth = localStorage.getItem("bedWidth");
    const savedDepth = localStorage.getItem("bedDepth");
    const savedSpacing = localStorage.getItem("partSpacing");
    const savedMargin = localStorage.getItem("searchMarginPercent");
    const savedCellSize = localStorage.getItem("cellSize");
    const savedEquipmentCost = localStorage.getItem("equipmentCost");
    const savedMaterialCost = localStorage.getItem("materialCost");

    if (savedWidth) bedWidth.value = parseInt(savedWidth);
    if (savedDepth) bedDepth.value = parseInt(savedDepth);
    if (savedSpacing) partSpacing.value = parseInt(savedSpacing);
    if (savedMargin) searchMarginPercent.value = parseInt(savedMargin);
    if (savedCellSize) cellSize.value = parseInt(savedCellSize);
    if (savedEquipmentCost) equipmentCost.value = parseInt(savedEquipmentCost);
    if (savedMaterialCost) materialCost.value = parseInt(savedMaterialCost);
  } catch (error) {
    console.warn("localStorage에서 설정값을 불러오는데 실패했습니다:", error);
  }
}

// 설정값을 localStorage에 저장하기
function saveSettings() {
  try {
    localStorage.setItem("bedWidth", bedWidth.value.toString());
    localStorage.setItem("bedDepth", bedDepth.value.toString());
    localStorage.setItem("partSpacing", partSpacing.value.toString());
    localStorage.setItem("searchMarginPercent", searchMarginPercent.value.toString());
    localStorage.setItem("cellSize", cellSize.value.toString());
    localStorage.setItem("equipmentCost", equipmentCost.value.toString());
    localStorage.setItem("materialCost", materialCost.value.toString());
  } catch (error) {
    console.warn("localStorage에 설정값을 저장하는데 실패했습니다:", error);
  }
}

const newBox = ref({w: 30, l: 30, h: 30});

let scene, camera, renderer, controls;
let requestId = null;

// 박스 리스트 JSON 저장
function saveBoxes() {
  const json = JSON.stringify(parts.value, null, 2);
  const blob = new Blob([json], {type: "application/json"});
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "box-list.json";
  a.click();
  URL.revokeObjectURL(url);
}

// 파일 선택 트리거
function triggerLoad() {
  fileInput.value && fileInput.value.click();
}

// 선택된 파일 읽어서 parts에 로드
function onFileSelected(event) {
  const file = event.target.files[0];
  if (!file) return;
  const reader = new FileReader();
  reader.onload = (e) => {
    try {
      const data = JSON.parse(e.target.result);
      if (Array.isArray(data)) {
        // 필요한 추가 검증 로직을 넣어도 좋습니다
        parts.value = data;
      } else {
        alert("유효한 박스 리스트 JSON이 아닙니다.");
      }
    } catch (err) {
      alert("JSON 파싱 오류: " + err.message);
    }
    // 파일 인풋 초기화
    event.target.value = null;
  };
  reader.readAsText(file);
}

// 애니메이션 루프 중단 및 렌더러 해제
function stopAnimationLoop() {
  if (requestId) cancelAnimationFrame(requestId);
  requestId = null;
  if (renderer) {
    renderer.forceContextLoss?.();
    renderer.domElement?.remove();
    renderer.dispose();
    renderer = null;
  }
}

// 씬 리소스 완전 해제
function disposeScene() {
  if (!scene) return;
  scene.traverse((obj) => {
    if (obj.isMesh || obj.isLineSegments) {
      if (obj.geometry) obj.geometry.dispose();
      if (obj.material) {
        if (Array.isArray(obj.material)) {
          obj.material.forEach((m) => m.dispose());
        } else {
          obj.material.dispose();
        }
      }
    }
  });
  if (controls) {
    controls.dispose();
    controls = null;
  }
  scene = null;
}

function addBox() {
  const {w, l, h} = newBox.value;
  if (w > 0 && l > 0 && h > 0) {
    const volume = w * l * h;
    parts.value.push({
      name: `박스${parts.value.length + 1}`,
      count: 1,
      dimensions: [w, l, h],
      volume: volume,
    });
  }
}

function removeBox(index) {
  parts.value.splice(index, 1);
}

function clearAllBoxes() {
  parts.value = [];
  // 색상 관리 시스템도 초기화
  // if (window.globalColorManager) {
  //   window.globalColorManager.reset();
  // }
}

// 색상 팔레트를 전역으로 공유
const colorPalette = [0xff6347, 0x4682b4, 0x3cb371, 0xffa500, 0x9370db, 0x00ced1, 0xdeb887];

// 전역 색상 관리 시스템
if (!window.globalColorManager) {
  window.globalColorManager = {
    nameColorMap: new Map(),
    colorIndex: 0,
    colorPalette: colorPalette,

    assignColorToModel(modelName) {
      if (!this.nameColorMap.has(modelName)) {
        this.nameColorMap.set(
          modelName,
          this.colorPalette[this.colorIndex % this.colorPalette.length]
        );
        this.colorIndex++;
      }
      return this.nameColorMap.get(modelName);
    },

    getModelColor(modelName) {
      const color = this.assignColorToModel(modelName);
      return `#${color.toString(16).padStart(6, "0")}`;
    },

    reset() {
      this.nameColorMap.clear();
      this.colorIndex = 0;
    },
  };
}

// 색상 할당 함수
function assignColorToModel(modelName) {
  return window.globalColorManager.assignColorToModel(modelName);
}

function addPa12ModelToPacking(modelData) {
  // 색상 할당
  const color = assignColorToModel(modelData.name);

  // 부피 계산하여 추가
  const modelWithVolume = {
    ...modelData,
    volume: modelData.volume,
    color: color, // 색상 정보 추가
  };
  parts.value.push(modelWithVolume);
  // 사용자에게 추가된 박스 정보 알림
  console.log(
    `박스 추가됨: ${modelData.name} - [${modelData.dimensions.join(", ")}] - 부피: ${
      modelData.volume
    }mm³ - 색상: #${color.toString(16)}`
  );
}

// 변경된 generateRandomParts 함수
function generateRandomParts(count, bedW, bedD, startIndex = 0) {
  const arr = [];
  for (let i = 0; i < count; i++) {
    // 배드 크기의 10%~50% 범위에서 랜덤 박스 크기 결정
    const maxW = Math.floor(bedW * 0.5);
    const maxL = Math.floor(bedD * 0.5);
    const w = Math.floor(Math.random() * (maxW - 10 + 1)) + 10;
    const l = Math.floor(Math.random() * (maxL - 10 + 1)) + 10;
    // 높이는 w, l 평균값 전후로 설정 (옵션)
    const h = Math.floor(((w + l) / 2) * (0.5 + Math.random() * 0.5));
    const volume = w * l * h;
    arr.push({
      name: `랜덤박스${startIndex + i + 1}`,
      count: 1,
      dimensions: [w, l, h],
      volume: volume,
    });
  }
  return arr;
}

const _rotationCache = new Map();
function getSurfaceRotations([w, l, h]) {
  const key = [w, l, h].sort((a, b) => a - b).join(",");
  if (_rotationCache.has(key)) return _rotationCache.get(key);

  // 가장 작은 값이 높이가 되고, 나머지 두 값으로 바닥면 결정
  const dims = [w, l, h];
  const sorted = [...dims].sort((a, b) => a - b);
  const height = sorted[0];
  const surface = sorted.slice(1);
  const rots = [
    [surface[0], surface[1], height],
    [surface[1], surface[0], height],
  ];
  _rotationCache.set(key, rots);
  return rots;
}

class HeightmapPacker {
  constructor(width, depth, cellSize = 1) {
    this.cellSize = cellSize;
    this.width = Math.ceil(width / cellSize);
    this.depth = Math.ceil(depth / cellSize);
    this.heightmap = new Uint16Array(this.width * this.depth);
    this.lastX = 0;
    this.lastY = 0;
    this._maxZ = 0;
  }
  getMaxZ(x, y, w, l) {
    let max = 0;
    for (let yy = y; yy < y + l; yy++) {
      const rowOff = yy * this.width;
      for (let xx = x; xx < x + w; xx++) {
        const z = this.heightmap[rowOff + xx];
        if (z > max) max = z;
      }
    }
    return max;
  }
  canPlace(x, y, w, l, baseZ, spacing) {
    for (let yy = y - spacing; yy < y + l + spacing; yy++) {
      if (yy < 0 || yy >= this.depth) continue;
      const rowOff = yy * this.width;
      for (let xx = x - spacing; xx < x + w + spacing; xx++) {
        if (xx < 0 || xx >= this.width) continue;
        if (this.heightmap[rowOff + xx] > baseZ) return false;
      }
    }
    return true;
  }
  place(x, y, w, l, h, spacing) {
    const baseZ = this.getMaxZ(x, y, w, l);
    const topZ = baseZ + h + spacing;
    for (let yy = y; yy < y + l; yy++) {
      const rowOff = yy * this.width;
      for (let xx = x; xx < x + w; xx++) {
        this.heightmap[rowOff + xx] = topZ;
      }
    }
    this.lastX = x;
    this.lastY = y;
    if (topZ > this._maxZ) this._maxZ = topZ;
  }
  findBest(w, l, margin, spacing) {
    const sx = Math.max(0, this.lastX - margin);
    const ex = Math.min(this.width - w, this.lastX + margin);
    const sy = Math.max(0, this.lastY - margin);
    const ey = Math.min(this.depth - l, this.lastY + margin);
    let best = null;
    for (let yy = sy; yy <= ey; yy++) {
      for (let xx = sx; xx <= ex; xx++) {
        const baseZ = this.getMaxZ(xx, yy, w, l);
        if (best && baseZ >= best.baseZ) continue;
        if (!this.canPlace(xx, yy, w, l, baseZ, spacing)) continue;
        best = {x: xx, y: yy, baseZ};
      }
    }
    return best;
  }
  getCurrentHeight() {
    return this._maxZ;
  }
}

function calculate3DPackingLayout(parts, boxDim, spacing = 0, margin = Infinity, cellSize = 5) {
  const [bedW, bedD] = boxDim;
  const toCells = (v) => Math.ceil(v / cellSize);
  const packer = new HeightmapPacker(bedW, bedD, cellSize);

  const items = parts
    .flatMap((p) =>
      Array(p.count).fill({
        dimensions: p.dimensions,
        name: p.name,
        color: p.color, // 색상 정보 추가
      })
    )
    .map(({dimensions, name, color}) => ({
      dims: dimensions,
      name,
      color, // 색상 정보 보존
      volume: dimensions[0] * dimensions[1] * dimensions[2],
    }))
    .sort((a, b) => b.volume - a.volume);

  const placed = [];
  for (const item of items) {
    const {dims, name, color} = item; // 색상 정보 추출
    const cands = [];
    for (const rot of getSurfaceRotations(dims)) {
      const [w, l, h] = rot;
      if (w > bedW || l > bedD) continue;
      const wC = toCells(w),
        lC = toCells(l),
        hC = toCells(h);
      const sC = toCells(spacing),
        mC = toCells(margin);
      const best = packer.findBest(wC, lC, mC, sC);
      if (best) {
        cands.push({
          rot,
          x: best.x,
          y: best.y,
          baseZ: best.baseZ,
          totalZ: best.baseZ + hC,
          cellDims: [wC, lC, hC],
        });
      }
    }
    if (!cands.length) return null;
    cands.sort((a, b) => a.totalZ - b.totalZ);
    const {
      rot,
      x,
      y,
      baseZ,
      cellDims: [wC, lC, hC],
    } = cands[0];
    const [w, l, origH] = rot;
    packer.place(x, y, wC, lC, hC, toCells(spacing));
    placed.push({
      position: [x * cellSize, y * cellSize, baseZ * cellSize],
      dims: [w, l, origH],
      name,
      color, // 색상 정보 추가
    });
  }

  // const finalHeight = packer.getCurrentHeight() * cellSize;
  // 기존 코드: const finalHeight = packer.getCurrentHeight() * cellSize;
  // 새로운 코드: 모든 박스의 top(z+height) 중 최대값을 사용
  let finalHeight = 0;
  if (placed.length > 0) {
    finalHeight = Math.max(...placed.map((part) => part.position[2] + part.dims[2]));
  }

  finalHeight = finalHeight + Number(process.env.VUE_APP_PARKING_ADD_HEIGHT);

  return {
    finalHeight: Math.ceil(Math.max(0, finalHeight)),
    packedParts: placed,
  };
}

function renderPackedParts(containerEl, result, bedDimensions, partSpacing) {
  // 이전 애니메이션·렌더러·씬 리소스 정리
  stopAnimationLoop();
  disposeScene();

  // 새 씬 생성
  scene = new THREE.Scene();
  scene.background = new THREE.Color(0xf0f0f0);
  camera = new THREE.PerspectiveCamera(
    60,
    containerEl.clientWidth / containerEl.clientHeight,
    1,
    10000
  );
  camera.position.set(600, 400, 600);

  renderer = new THREE.WebGLRenderer({antialias: true});
  renderer.setSize(containerEl.clientWidth, containerEl.clientHeight);
  containerEl.appendChild(renderer.domElement);

  controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;

  // 배드 (바닥)
  const bed = new THREE.Mesh(
    new THREE.BoxGeometry(bedDimensions[0], 1, bedDimensions[1]),
    new THREE.MeshStandardMaterial({color: 0xcccccc})
  );
  bed.position.set(bedDimensions[0] / 2, 0, bedDimensions[1] / 2);
  scene.add(bed);

  for (const part of result.packedParts) {
    const [w, l, h] = part.dims;
    const geometry = new THREE.BoxGeometry(w, h, l);

    // 색상 결정: part에 color가 있으면 사용, 없으면 전역 시스템 사용
    let color;
    if (part.color) {
      color = part.color;
      console.log(`박스 "${part.name}" 색상: #${color.toString(16)} (저장된 색상 사용)`);
    } else {
      color = window.globalColorManager.assignColorToModel(part.name);
      console.log(`박스 "${part.name}" 색상: #${color.toString(16)} (전역 시스템에서 할당)`);
    }

    const material = new THREE.MeshStandardMaterial({
      color: color,
      transparent: true,
      opacity: 0.9,
    });
    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.set(part.position[0] + w / 2, part.position[2] + h / 2, part.position[1] + l / 2);
    scene.add(mesh);

    const edges = new THREE.EdgesGeometry(geometry);
    const line = new THREE.LineSegments(edges, new THREE.LineBasicMaterial({color: 0x000000}));
    line.position.copy(mesh.position);
    scene.add(line);
  }

  scene.add(new THREE.AmbientLight(0x888888));
  const directional = new THREE.DirectionalLight(0xffffff, 0.8);
  directional.position.set(500, 1000, 500);
  scene.add(directional);

  function animate() {
    requestId = requestAnimationFrame(animate);
    controls.update();
    renderer.render(scene, camera);
  }
  animate();
}

function startPacking() {
  const rnd =
    boxCount.value > 0
      ? generateRandomParts(boxCount.value, bedWidth.value, bedDepth.value, parts.value.length)
      : [];
  parts.value.push(...rnd);
  // percentage → mm
  const marginMm = (Math.max(bedWidth.value, bedDepth.value) * searchMarginPercent.value) / 100;
  const t0 = performance.now();
  const out = calculate3DPackingLayout(
    parts.value,
    [bedWidth.value, bedDepth.value],
    partSpacing.value,
    marginMm,
    cellSize.value
  );
  const t1 = performance.now();

  if (out) {
    // 저장된 부피값들을 합산
    const groupParts = new Map();
    for (const part of parts.value) {
      const group = groupParts.get(part.name);
      if (group) {
        group.push(part);
      } else {
        groupParts.set(part.name, [part]);
      }
    }

    const config = {
      step: 5,
      costPerGram: materialCost.value,
    };
    let finalMaterialCost = 0;
    let totalWeight = 0;
    for (const group of groupParts.values()) {
      // totalVolume = group.reduce((sum, part) => sum + (part.volume || 0), 0);
      const aPartMaterialCost = calculateMaterialCost({volume: group[0].volume}, config);
      finalMaterialCost += aPartMaterialCost.totalCost * group.length;
      totalWeight += aPartMaterialCost.roundedWeight * group.length;
    }

    // const totalVolume = parts.value.reduce((sum, part) => sum + (part.volume || 0), 0);
    // const config = {
    //   step: 5,
    //   costPerGram: materialCost.value,
    // };
    // const finalMaterialCost = calculateMaterialCost({volume: totalVolume}, config);
    // const finalCost = out.finalHeight * equipmentCost.value + finalMaterialCost.totalCost;
    const finalCost = out.finalHeight * equipmentCost.value + finalMaterialCost;

    if (0 === finalMaterialCost) {
      timingInfo.value = `✅ 배치 시간: ${(0).toFixed(
        2
      )}ms | 박스: ${0}개 | 높이: ${0}mm | 전체무게: ${0}g | 예상금액: ${0}원`;

      return;
    }


    // (t1 - t0).toFixed(2);
    // out.packedParts.length;
    // out.finalHeight;
    // searchMarginPercent.value + "%";
    // cellSize.value;
    // totalWeight;
    // finalCost;

    results.value.push({
      time: (t1 - t0).toFixed(2),
      count: out.packedParts.length,
      height: out.finalHeight,
      margin: searchMarginPercent.value + "%",
      cellSize: cellSize.value,
      totalVolume: totalWeight,
      finalCost: finalCost,
    });
    timingInfo.value = `✅ 배치 시간: ${(t1 - t0).toFixed(2)}ms | 박스: ${
      out.packedParts.length
    }개 | 높이: ${out.finalHeight}mm (${
      out.finalHeight - Number(process.env.VUE_APP_PARKING_ADD_HEIGHT)
    }mm) | 전체무게: ${totalWeight}g | 예상금액: ${numberToComma(finalCost)}원`;
    renderPackedParts(container.value, out, [bedWidth.value, bedDepth.value], partSpacing.value);
  } else {
    timingInfo.value = `❌ Packing Failed`;
  }
}

onMounted(() => {
  loadSettings(); // 페이지 로드 시 저장된 설정값 불러오기
  startPacking();
});

onBeforeUnmount(() => {
  stopAnimationLoop();
  disposeScene();
});
</script>

<style scoped>
.viewer-container {
  width: 100%;
  height: 100vh;
  background: #f0f0f0;
  position: relative;
}

.info {
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 100;
  background: rgba(255, 255, 255, 0.9);
  padding: 8px 12px;
  font-size: 14px;
  font-family: monospace;
  border-radius: 4px;
  box-shadow: 0 0 6px rgba(0, 0, 0, 0.2);
  color: #000;
}

.controls {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 350px;
  z-index: 100;
  padding: 12px;
  border-radius: 5px;
  background-color: white;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.2);
}

.controls-row {
  display: flex;
  align-items: center;
  margin-bottom: 9px;
  padding-bottom: 9px;
  gap: 8px;
  color: #000;
  border-bottom: 1px solid #dadada;
  font-size: 0.9rem;
}

label {
  min-width: 100px;
  color: #000;
}

input {
  width: 60px;
}

.box-list {
  margin-top: 8px;
  font-size: 0.9rem;
  color: #000;
  background: #fff;
  padding: 10px;
  border-radius: 4px;
  min-height: 100px;
  max-height: 400px;
  overflow-y: auto;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.2);
}

.box-list ul {
  padding-top: 5px;
  /* padding-left: 16px; */
  margin: 0;
}

.box-list li {
  margin-bottom: 4px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1px 0;
}

.remove-btn {
  margin-left: 8px;
  font-size: 11px;
  padding: 3px 6px;
  background: #ff4d4d;
  color: #fff;
  border: none;
  border-radius: 3px;
  cursor: pointer;
}

.remove-btn:hover {
  background: #cc0000;
}

.results-list {
  position: absolute;
  bottom: 10px;
  left: 10px;
  min-width: 500px;
  max-height: 220px;
  overflow-y: auto;
  background: white;
  padding: 8px;
  border-radius: 6px;
  box-shadow: 0 0 6px rgba(0, 0, 0, 0.2);
  font-size: 13px;
  color: #000;
  z-index: 100;
}

.results-list li {
  padding: 3px 0;
}
</style>
